import { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "../../lib/api";
import { Search, X, Loader2, FileText } from "lucide-react";

/**
 * Global docs search overlay. Mounted at the docs layout level.
 * Triggered by keyboard (Cmd/Ctrl+K) or by clicking the search button in the sidebar.
 */
export function DocsSearch({ open, onClose, theme = "dark" }) {
  const [q, setQ] = useState("");
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();
  const isLight = theme === "light";

  useEffect(() => {
    if (!open) return;
    setQ("");
    setResults([]);
  }, [open]);

  const run = useCallback(async (term) => {
    if (!term || term.length < 2) {
      setResults([]);
      return;
    }
    setLoading(true);
    try {
      const r = await api.get("/docs/search", { params: { q: term } });
      setResults(r.data.results || []);
    } catch (_) {
      setResults([]);
    } finally { setLoading(false); }
  }, []);

  useEffect(() => {
    const t = setTimeout(() => run(q), 180);
    return () => clearTimeout(t);
  }, [q, run]);

  if (!open) return null;

  // Tone-aware tokens
  const backdrop = isLight ? "bg-aubergine/30" : "bg-aubergine/70";
  const panel = isLight
    ? "border-aubergine/12 bg-bone-soft"
    : "border-champ-light/15 bg-aubergine-tier";
  const header = isLight ? "border-b border-aubergine/10" : "border-b border-champ-light/10";
  const iconMuted = isLight ? "text-aubergine/55" : "text-bone/55";
  const input = isLight
    ? "text-aubergine placeholder:text-aubergine/35"
    : "text-bone placeholder:text-bone/35";
  const closeBtn = isLight ? "text-aubergine/45 hover:text-aubergine" : "text-bone/45 hover:text-bone";
  const dividerCls = isLight ? "divide-y divide-aubergine/10" : "divide-y divide-champ-light/10";
  const resultHover = isLight ? "hover:bg-bone" : "hover:bg-aubergine-raised";
  const titleColor = isLight ? "text-aubergine" : "text-bone";
  const metaColor = isLight ? "text-aubergine/55" : "text-bone/45";
  const snippetColor = isLight ? "text-aubergine/70" : "text-bone/65";
  const footer = isLight
    ? "border-t border-aubergine/10 text-aubergine/45"
    : "border-t border-champ-light/10 text-bone/35";

  const renderSnippet = (snippet, term) => {
    if (!snippet || !term) return snippet;
    const idx = snippet.toLowerCase().indexOf(term.toLowerCase());
    if (idx === -1) return snippet;
    const markCls = isLight
      ? "bg-champagne/30 text-aubergine px-0.5 rounded-sm"
      : "bg-champagne/30 text-bone px-0.5 rounded-sm";
    return (
      <>
        {snippet.slice(0, idx)}
        <mark className={markCls}>{snippet.slice(idx, idx + term.length)}</mark>
        {snippet.slice(idx + term.length)}
      </>
    );
  };

  return (
    <div
      role="dialog"
      aria-label="Search docs"
      className={`fixed inset-0 z-50 flex items-start justify-center px-4 pt-24 backdrop-blur-sm ${backdrop}`}
      onMouseDown={onClose}
      data-testid="docs-search-overlay"
    >
      <div
        className={`w-full max-w-2xl rounded-md border shadow-2xl overflow-hidden ${panel}`}
        onMouseDown={(e) => e.stopPropagation()}
      >
        <div className={`flex items-center gap-3 px-5 py-3.5 ${header}`}>
          <Search className={`h-4 w-4 flex-shrink-0 ${iconMuted}`} />
          <input
            autoFocus
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Search docs…"
            data-testid="docs-search-input"
            onKeyDown={(e) => { if (e.key === "Escape") onClose(); }}
            className={`flex-1 bg-transparent outline-none text-sm ${input}`}
          />
          {loading && <Loader2 className={`h-3.5 w-3.5 animate-spin ${iconMuted}`} />}
          <button onClick={onClose} data-testid="docs-search-close" className={closeBtn}>
            <X className="h-4 w-4" />
          </button>
        </div>
        <div className="max-h-[55vh] overflow-y-auto">
          {q.length < 2 ? (
            <div className={`px-5 py-10 text-center text-sm ${metaColor}`} data-testid="docs-search-hint">Type at least 2 characters…</div>
          ) : results.length === 0 ? (
            <div className={`px-5 py-10 text-center text-sm ${metaColor}`} data-testid="docs-search-empty">
              No results for &ldquo;<span className={titleColor}>{q}</span>&rdquo;
            </div>
          ) : (
            <ul className={dividerCls}>
              {results.map((r) => (
                <li key={r.page_id}>
                  <button
                    onClick={() => { navigate(`/docs/${r.section_slug}/${r.slug}`); onClose(); }}
                    data-testid={`docs-search-result-${r.page_id}`}
                    className={`w-full text-left px-5 py-3.5 transition-colors flex gap-3 ${resultHover}`}
                  >
                    <FileText className="h-4 w-4 text-champagne mt-0.5 flex-shrink-0" />
                    <div className="min-w-0 flex-1">
                      <p className={`text-sm font-medium ${titleColor}`}>{r.title}</p>
                      <p className={`font-mono text-[10px] uppercase tracking-wider mt-0.5 ${metaColor}`}>{r.section_slug.replace(/-/g, " ")}</p>
                      {r.snippet && (
                        <p className={`text-xs mt-1.5 line-clamp-2 leading-relaxed ${snippetColor}`}>{renderSnippet(r.snippet, q)}</p>
                      )}
                    </div>
                  </button>
                </li>
              ))}
            </ul>
          )}
        </div>
        <div className={`px-5 py-2.5 flex items-center justify-between text-[10px] font-mono uppercase tracking-wider ${footer}`}>
          <span>{results.length > 0 ? `${results.length} results` : "Search"}</span>
          <span>esc to close</span>
        </div>
      </div>
    </div>
  );
}
